<template>
  <div>
    <Card class="h-full">
      <CardHeader>
        <CardTitle>Offers ready to activate</CardTitle>
        <CardDescription>Deactivated offers, newest first</CardDescription>
        <CardAction>
          <Button variant="outline" size="sm" @click="goToProgram">
            View program
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SharedDataTable
          :headers="headers"
          :items="offers"
          :loading="loading"
          :error="error"
          @retry="fetchOffers()"
          @row-click="viewOffer"
          :default-rows-per-page="10"
          :rows-items="[10, 25, 50]"
        >
          <template #item-offer="row">
            <div class="flex items-center gap-2">
              <img
                v-if="logo(row)"
                :src="logo(row)"
                :alt="retailerName(row)"
                class="w-6 h-6 rounded border border-border object-contain shrink-0 bg-white"
              />
              <div
                v-else
                class="w-6 h-6 rounded border border-border bg-muted shrink-0"
              />
              <div class="min-w-0">
                <p class="text-sm font-medium leading-tight truncate">{{ retailerName(row) }}</p>
              </div>
            </div>
          </template>

          <template #item-cashback="row">
            <div class="space-y-0.5">
              <p class="text-sm font-semibold tabular-nums">{{ cashbackLabel(row) }}</p>
              <!-- Only surfaced when a cap actually exists; an uncapped offer says nothing. -->
              <p v-if="capLabel(row)" class="text-xs text-muted-foreground">{{ capLabel(row) }}</p>
            </div>
          </template>

          <template #item-status="row">
            <span class="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
              <CircleSlash class="size-3.5 text-muted-foreground" />
              {{ row.active ? 'Live' : 'Deactivated' }}
            </span>
          </template>

          <template #item-action="row">
            <div class="w-full text-right">
              <!-- .stop so the click is not also handled by the row-click above -->
              <Button variant="outline" size="sm" @click.stop="viewOffer(row)">View</Button>
            </div>
          </template>

          <template #empty-message>
            <div class="flex justify-center py-4">
              <span class="text-sm text-muted-foreground">No deactivated offers to activate.</span>
            </div>
          </template>
        </SharedDataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable as SharedDataTable,
} from '@boogi/ui'
import { CircleSlash } from 'lucide-vue-next'

export default {
  name: 'OffersToActivate',
  components: {
    SharedDataTable,
    Button,
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CircleSlash,
  },
  data() {
    return {
      loading: true,
      error: false,
      offers: [],
      headers: [
        { text: 'Offer', value: 'offer' },
        { text: 'Cashback', value: 'cashback' },
        { text: 'Status', value: 'status' },
        { text: 'Action', value: 'action', align: 'right' },
      ],
    }
  },
  created() {
    this.fetchOffers()
  },
  methods: {
    retailerName(row) {
      return (row.retailer && row.retailer.retailerName) || 'Unknown retailer'
    },
    logo(row) {
      return (row.offer && row.offer.displayInformation && row.offer.displayInformation.logo) || ''
    },
    cashbackLabel(row) {
      const amount = row.displayRules && row.displayRules[0] && row.displayRules[0].highestCashbackPercentageAmount
      if (amount == null) return '—'
      const type =
        row.cashbackEarnings &&
        row.cashbackEarnings[0] &&
        row.cashbackEarnings[0].conditions &&
        row.cashbackEarnings[0].conditions[0] &&
        row.cashbackEarnings[0].conditions[0].value &&
        row.cashbackEarnings[0].conditions[0].value.type
      return type === 'PERCENTAGE' ? `${amount}%` : `£${amount}`
    },
    capLabel(row) {
      const offer = row.offer || {}
      if (offer.spendingCapped || offer.spendingCapAmount > 0) {
        return `Capped at £${offer.spendingCapAmount} per user`
      }
      return ''
    },
    viewOffer(row) {
      const retailerId = row.retailer && row.retailer.id
      const offerId = row.offer && row.offer.id
      if (retailerId && offerId) this.$router.push(`/retailer/${retailerId}/offer/${offerId}`)
    },
    goToProgram() {
      this.$router.push('/cashback-program/program-offers')
    },
    async fetchOffers() {
      this.loading = true
      this.error = false
      try {
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const token = localStorage.getItem('token')
        const res = await this.$apiFetch(
          `${apiUrl}/private/api/client/activated-offer/deactivated?page=0&size=100`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )
        if (res.ok) {
          const obj = await res.json()
          this.offers = obj.content || []
        } else {
          this.error = true
        }
      } catch (e) {
        this.offers = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
